// Background video upload queue: keeps uploading while the user navigates anywhere in the app.
// jobs: { id, conversationId, conversationName, name, status: queued|compressing|uploading|done|error, progress 0..1 }
import { toast } from "sonner";
import { api, sendMessage, uploadFileChunked } from "../api";
import { makeVideoPoster } from "./videoPoster";
import { compressPickedVideo } from "./videoCompress";

let jobs = [];
const listeners = new Set();
let running = false;
let doneLabel = (name, kind) => `${kind === "photo" ? "Photo" : "Video"} sent to ${name}`;
let failLabel = (name, kind) => `${kind === "photo" ? "Photo" : "Video"} failed — ${name}`;
let retryLabel = "Retry";

export function setUploadQueueLabels({ done, fail, retry }) { doneLabel = done || doneLabel; failLabel = fail || failLabel; retryLabel = retry || retryLabel; }
export function subscribeUploads(fn) { listeners.add(fn); fn(jobs); return () => listeners.delete(fn); }
const emit = () => listeners.forEach((fn) => fn([...jobs]));
const patch = (id, data) => { jobs = jobs.map((j) => (j.id === id ? { ...j, ...data } : j)); emit(); };

const newId = () => `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** source: File/Blob OR { picked } (native gallery pick, compressed inside the queue). quality: "fast" (720p) | "original". */
export function enqueueVideoUpload({ conversationId, conversationName, senderId, jobId = null, source, picked = null, meta = null, replyTo = null, quality = "fast" }) {
  const id = newId();
  jobs = [...jobs, { id, kind: "video", conversationId, conversationName, senderId, jobId, source, picked, meta, replyTo, quality, name: source?.name || picked?.name || "video", status: "queued", progress: 0 }];
  emit();
  if (!running) run();
  return id;
}

/** Generic background task (photos): run(onProgress) does upload + send. */
export function enqueueTask({ kind = "photo", conversationId, conversationName, run: task }) {
  const id = newId();
  jobs = [...jobs, { id, kind, conversationId, conversationName, run: task, status: "queued", progress: 0 }];
  emit();
  if (!running) run();
  return id;
}

/** Tap on a failed upload → send it again without picking the file again. */
export function retryUpload(id) {
  const j = jobs.find((x) => x.id === id && x.status === "error");
  if (!j) return;
  patch(id, { status: "queued", progress: 0, error: null });
  if (!running) run();
}

export function dismissUpload(id) { jobs = jobs.filter((j) => j.id !== id); emit(); }

export const activeUploads = () => jobs.filter((j) => j.status !== "done" && j.status !== "error");

async function run() {
  running = true;
  try {
    for (;;) {
      const job = jobs.find((j) => j.status === "queued");
      if (!job) break;
      await processJob(job);
    }
  } finally {
    running = false;
  }
}

async function processJob(job) {
  try {
    if (job.run) {
      patch(job.id, { status: "uploading", progress: 0 });
      await job.run((p) => patch(job.id, { progress: p }));
      patch(job.id, { status: "done", progress: 1 });
      toast.success(doneLabel(job.conversationName || "", job.kind), { duration: 4000 });
      return;
    }
    let file = job.source;
    if (!file && job.picked) {
      patch(job.id, { status: "compressing", progress: 0 });
      const r = await compressPickedVideo(job.picked, (p) => patch(job.id, { progress: p }), { skip: job.quality === "original" });
      file = r.file;
    }
    patch(job.id, { status: "uploading", progress: 0 });
    const posterPromise = makeVideoPoster(file).catch(() => null);
    const media = await uploadFileChunked({
      file, kind: "video", jobId: job.jobId, uploadedBy: job.senderId,
      onProgress: (p) => patch(job.id, { progress: p }),
    });
    api.post(`/media/${media.id}/captions/auto`).catch(() => {});
    try {
      const poster = await posterPromise;
      if (poster) await api.post(`/media/${media.id}/poster`, { data_url: poster });
    } catch (e) { /* best effort */ }
    await sendMessage({
      conversation_id: job.conversationId, sender_id: job.senderId, type: "video",
      content: "", media_ids: [media.id], meta: job.meta || {}, reply_to: job.replyTo || null,
    });
    patch(job.id, { status: "done", progress: 1 });
    toast.success(doneLabel(job.conversationName || "", job.kind), { duration: 5000 });
  } catch (e) {
    patch(job.id, { status: "error", error: String(e?.message || e) });
    toast.error(failLabel(job.conversationName || "", job.kind), { duration: 10000, action: { label: retryLabel, onClick: () => retryUpload(job.id) } });
  } finally {
    // done → disappears shortly; error → stays in the bar until retried/dismissed
    setTimeout(() => { if (jobs.find((j) => j.id === job.id)?.status === "done") { jobs = jobs.filter((j) => j.id !== job.id); emit(); } }, 2500);
  }
}
